import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Building2, Briefcase, ArrowRight, Info, Search, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";

// Circular WOWSHE-branded pin using the brand mark
const wowsheIcon = L.divIcon({
  className: "wowshe-pin",
  html: `
    <div style="width:44px;height:44px;border-radius:50%;overflow:hidden;background:#FFF8F0;border:3px solid #FFF8F0;box-shadow:0 6px 14px rgba(58,15,77,.35),0 0 0 2px rgba(157,43,107,.6)">
      <img src="/wowshe-mark.png" alt="" style="width:100%;height:100%;object-fit:cover;display:block" />
    </div>
  `,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
  popupAnchor: [0, -24],
});

export default function MembersMap() {
  const { user } = useAuth();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [chapter, setChapter] = useState("");

  useEffect(() => {
    api.get("/members-map").then((r) => setPoints(r.data)).catch(() => setPoints([])).finally(() => setLoading(false));
  }, []);

  // Unique filter values from data
  const { categories, chapters } = useMemo(() => {
    const cats = new Set();
    const chs = new Set();
    points.forEach((p) => {
      if (p.industry) cats.add(p.industry);
      if (p.chapter) chs.add(p.chapter);
    });
    return { categories: Array.from(cats).sort(), chapters: Array.from(chs).sort() };
  }, [points]);

  // Filtered points
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return points.filter((p) => {
      if (category && p.industry !== category) return false;
      if (chapter && p.chapter !== chapter) return false;
      if (!q) return true;
      const hay = `${p.name || ""} ${p.business || ""} ${p.industry || ""} ${p.chapter || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [points, query, category, chapter]);

  // Centre on India by default, but if we have points use their centroid
  const center = useMemo(() => {
    const data = filtered.length ? filtered : points;
    if (!data.length) return [22.5, 78.9];
    const avgLat = data.reduce((s, p) => s + p.office_lat, 0) / data.length;
    const avgLng = data.reduce((s, p) => s + p.office_lng, 0) / data.length;
    return [avgLat, avgLng];
  }, [filtered, points]);

  const mapKey = `${center[0]}-${center[1]}-${filtered.length}`;
  const role = user?.role === "admin" ? "admin" : "member";
  const hasFilters = query || category || chapter;

  return (
    <DashboardShell role={role} title="Members Map" subtitle="Discover member offices across India.">
      <div data-testid="members-map-page" className="space-y-5">
        {/* Search & Filters */}
        <div className="rounded-2xl border border-aubergine/10 bg-white p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          {/* Search input */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-plumSoft/60" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, business, category or chapter…"
              data-testid="map-search-input"
              className="w-full pl-10 pr-9 py-2.5 rounded-full border border-aubergine/10 text-sm text-aubergine bg-lavender/20 focus:outline-none focus:border-aubergine/30 placeholder-plumSoft/60"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-plumSoft/60 hover:text-aubergine" aria-label="Clear search">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category filter */}
          <div className="relative shrink-0">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="map-filter-category"
              className="appearance-none pl-4 pr-10 py-2.5 rounded-full border border-aubergine/10 text-sm text-aubergine bg-white focus:outline-none focus:border-aubergine/30 min-w-[170px] cursor-pointer hover:border-aubergine/20 transition-colors"
            >
              <option value="">All categories</option>
              {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-plumSoft/70 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Chapter filter */}
          <div className="relative shrink-0">
            <select
              value={chapter}
              onChange={(e) => setChapter(e.target.value)}
              data-testid="map-filter-chapter"
              className="appearance-none pl-4 pr-10 py-2.5 rounded-full border border-aubergine/10 text-sm text-aubergine bg-white focus:outline-none focus:border-aubergine/30 min-w-[150px] cursor-pointer hover:border-aubergine/20 transition-colors"
            >
              <option value="">All chapters</option>
              {chapters.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-plumSoft/70 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Reset + result count */}
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[11px] uppercase tracking-widest text-plum" data-testid="map-result-count">
              {filtered.length} of {points.length}
            </span>
            {hasFilters && (
              <button
                onClick={() => { setQuery(""); setCategory(""); setChapter(""); }}
                data-testid="map-reset-filters"
                className="text-[11px] uppercase tracking-widest text-rose hover:text-aubergine flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Privacy note */}
        <div className="rounded-2xl border border-aubergine/8 bg-white p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-lavender/60 text-plum flex items-center justify-center shrink-0">
            <Info className="w-4 h-4" />
          </div>
          <div className="text-sm text-plumSoft font-light">
            Only members who have <strong className="text-aubergine font-medium">opted to share their location</strong> appear here. Update yours from <Link to="/dashboard?tab=profile" className="link-underline text-plum">Profile → Open rich editor → Office location</Link>.
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden border border-aubergine/10 shadow-luxe bg-white">
          <div className="h-[560px] relative">
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center text-plumSoft">Loading map…</div>
            ) : filtered.length === 0 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-plumSoft">
                <Search className="w-8 h-8 text-plumSoft/40" />
                <p className="text-sm">No members match these filters.</p>
                <button onClick={() => { setQuery(""); setCategory(""); setChapter(""); }} className="text-xs text-rose hover:underline">Reset filters</button>
              </div>
            ) : (
              <MapContainer key={mapKey} center={center} zoom={filtered.length > 1 ? 5 : 11} scrollWheelZoom className="h-full w-full">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {filtered.map((p) => (
                  <Marker key={p.id} position={[p.office_lat, p.office_lng]} icon={wowsheIcon}>
                    {p.industry && (
                      <Tooltip permanent direction="top" offset={[0, -24]} className="wowshe-tooltip">
                        {p.industry}
                      </Tooltip>
                    )}
                    <Popup>
                      <div className="min-w-[200px]" data-testid={`pin-${p.id}`}>
                        <div className="flex items-center gap-3">
                          {p.avatar ? (
                            <img src={p.avatar} alt={p.name} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-lavender text-plum flex items-center justify-center font-display text-lg">{p.name?.[0]}</div>
                          )}
                          <div className="min-w-0">
                            <p className="font-display text-base text-aubergine truncate !my-0">{p.name}</p>
                            <p className="text-xs text-plumSoft truncate !my-0">{p.business || "—"}</p>
                          </div>
                        </div>
                        <div className="mt-3 space-y-1 text-xs text-plum">
                          {p.industry && <div className="flex items-center gap-1.5"><Briefcase className="w-3 h-3" />{p.industry}</div>}
                          {p.chapter && <div className="flex items-center gap-1.5"><Building2 className="w-3 h-3" />{p.chapter}</div>}
                          {p.office_address && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{p.office_address}</div>}
                        </div>
                        <Link to={`/members/${p.id}`} className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-rose hover:text-aubergine">
                          View profile <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
